import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

interface EditableTextSectionProps {
  title: string;
  /** Currently saved value (from server). The input is dirty when local edits differ from this. */
  value: string;
  /** Persist the new value. Should also trigger any necessary cache invalidation. */
  onSave: (value: string) => Promise<unknown>;
  placeholder?: string;
  /** Renders a 3-row textarea instead of a single-line input. */
  multiline?: boolean;
}

/**
 * Card with a title and a single editable text field (input or textarea).
 * Save button disables when the value matches what's saved, and a brief
 * "Gespeichert" confirmation flashes after a successful save.
 */
export default function EditableTextSection({
  title,
  value: savedValue,
  onSave,
  placeholder,
  multiline = false,
}: EditableTextSectionProps) {
  const [value, setValue] = useState(savedValue);
  const [savedFlash, setSavedFlash] = useState(false);
  const flashTimerRef = useRef<number | null>(null);
  const dirty = value !== savedValue;

  const mutation = useMutation({
    mutationFn: () => onSave(value),
    onSuccess: () => {
      if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
      setSavedFlash(true);
      flashTimerRef.current = window.setTimeout(() => setSavedFlash(false), 2000);
    },
  });

  useEffect(() => {
    return () => {
      if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    };
  }, []);

  // Hide the confirmation as soon as the user starts editing again.
  const showSavedFlash = savedFlash && !dirty;

  const fieldClass =
    'w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none';

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <h3 className="text-lg font-medium">{title}</h3>
      </div>
      <div className="px-6 py-4 space-y-4">
        {multiline ? (
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            rows={3}
            className={`${fieldClass} resize-none`}
          />
        ) : (
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            className={fieldClass}
          />
        )}
        <div className="flex justify-end items-center gap-3">
          {showSavedFlash && (
            <span className="text-sm text-green-600 inline-flex items-center gap-1 transition-opacity">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Gespeichert
            </span>
          )}
          {mutation.isError && (
            <span className="text-sm text-red-600">Fehler beim Speichern.</span>
          )}
          <button
            onClick={() => mutation.mutate()}
            disabled={!dirty || mutation.isPending}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? 'Wird gespeichert...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
